import { useState } from "react";

import { Drawer } from "@/components/ui/Modal.jsx";
import Button from "@/components/ui/Button.jsx";
import { Callout } from "@/components/ui/Display.jsx";
import { ProductivityTag } from "@/components/ui/Tag.jsx";
import { PRODUCTIVITY } from "@/lib/tokens.js";
import { idOf } from "@/lib/productivityRules.js";
import { cx } from "@/lib/cx.js";

/*
 * Per-team status for one rule.
 *
 * "Default" is not a status — it means no teamRules entry, so the team follows
 * globalStatus. That distinction matters: an explicit override pinned to the
 * same value as the default stops tracking it when the default later changes.
 *
 * Saved as the whole teamRules array in one PATCH, for the same reason team
 * members are: a session that adds one override and removes another must not
 * half-apply.
 */
const OPTIONS = ["default", ...Object.keys(PRODUCTIVITY)];

export default function OverridesDrawer({ rule, teams = [], onClose, onSave, saving }) {
    const [overrides, setOverrides] = useState(() => {
        const map = {};
        for (const tr of rule?.teamRules ?? []) {
            const id = idOf(tr.teamId);
            if (id) map[id] = tr.status;
        }
        return map;
    });

    const set = (teamId, value) =>
        setOverrides((current) => {
            const next = { ...current };
            if (value === "default") delete next[teamId];
            else next[teamId] = value;
            return next;
        });

    const original = {};
    for (const tr of rule?.teamRules ?? []) {
        const id = idOf(tr.teamId);
        if (id) original[id] = tr.status;
    }
    const dirty = JSON.stringify(original) !== JSON.stringify(overrides);

    const save = () =>
        onSave(
            Object.entries(overrides).map(([teamId, status]) => ({ teamId, status })),
        );

    return (
        <Drawer
            open
            onClose={onClose}
            title="Team overrides"
            subtitle={rule?.name}
            footer={
                <>
                    <Button onClick={onClose} disabled={saving}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={save}
                        disabled={!dirty}
                        loading={saving}
                        data-save-overrides
                    >
                        Save overrides
                    </Button>
                </>
            }
        >
            <div className="space-y-4">
                <Callout tone="info">
                    Every team follows the rule's default —{" "}
                    <ProductivityTag status={rule?.globalStatus ?? "unrated"} /> — unless you set
                    one here. Leaving a team on <strong className="font-semibold">Default</strong>{" "}
                    keeps it following that value if the default changes later.
                </Callout>

                {teams.length ? (
                    <div className="rounded-lg border border-slate-200 dark:border-slate-800">
                        {teams.map((team) => {
                            const value = overrides[team._id] ?? "default";
                            return (
                                <div
                                    key={team._id}
                                    data-override={team._id}
                                    className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-3.5 py-3 last:border-0 dark:border-slate-800"
                                >
                                    <span className="min-w-0 flex-1 truncate text-[13px] font-medium">
                                        {team.name}
                                    </span>

                                    <span className="flex flex-wrap gap-1">
                                        {OPTIONS.map((option) => (
                                            <button
                                                key={option}
                                                type="button"
                                                data-set={`${team._id}:${option}`}
                                                aria-pressed={value === option}
                                                onClick={() => set(team._id, option)}
                                                className={cx(
                                                    "rounded-md border px-2 py-1 text-[11.5px] font-medium transition",
                                                    value === option
                                                        ? "border-brand-500 bg-brand-50 text-brand-700 dark:bg-brand-950 dark:text-brand-200"
                                                        : "border-slate-200 text-slate-500 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800",
                                                )}
                                            >
                                                {option === "default"
                                                    ? "Default"
                                                    : PRODUCTIVITY[option].label}
                                            </button>
                                        ))}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                ) : (
                    <p className="py-6 text-center text-[13px] text-slate-400 dark:text-slate-500">
                        No teams yet — create one before setting overrides.
                    </p>
                )}
            </div>
        </Drawer>
    );
}
